import {ResourcePageRequestOptions} from '../../../core/services/resource-page-state.service';
import {UserSettingsWriterService} from '../../../core/services/user-settings-writer.service';
import {Injectable} from '@angular/core';
import {UserRestService} from "../../../api/services/user.rest.service";
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap} from "rxjs/operators";
import {UserApiModel} from "../../../api/models/user.api-model";
import {UserPasswordViewModel} from "./new-password/user-password.view-model";
import {UserViewModel} from "./user.view-model";
import {CurrentUserViewModel} from "../../../core/models/current-user.view-model";
import {MediaObjectRestService} from "../../../api/services/media-object.rest.service";
import {MediaObjectApiModel} from "../../../api/models/media-object.api-model";
import {MediaObjectViewModel} from "../../../core/models/media-object.view-model";
import {QueryParamsApiModel} from "../../../api/models/query-params-api.model";
import {FilterParameterApiModel} from "../../../api/models/filter-parameter.api.model";
import {ResultListApiModel} from "../../../api/models/result-list.api.model";
import {Router} from "@angular/router";

@Injectable()
export class UserDataProviderService {

  constructor(
    private us: UserRestService,
    private mors: MediaObjectRestService,
    private router: Router,
    private readonly settingsWriter: UserSettingsWriterService,
  ) {
  }

  public getUser(id: string): Observable<UserViewModel> {
    return this.us.get(id).pipe(map((userApiModel: UserApiModel) => new UserViewModel(userApiModel)))
  }

  public getUserWithMedia(id: string, options?: ResourcePageRequestOptions): Observable<UserViewModel> {
    return this.us.get(id, options).pipe(
      mergeMap((userApiModel: UserApiModel) => {
        const userVM = new UserViewModel(userApiModel);
        const params = new QueryParamsApiModel();
        params.filters = [...new Set([userApiModel.avatar, userApiModel.background]
          .filter((value): value is string => typeof value === 'string'))]
          .map(iri => new FilterParameterApiModel('id', iri));
        if (!params.filters.length) {
          return of(userVM);
        }
        return this.mors.getMediaObjectCollection(params)
          .pipe(map((mediaObjectResultList: ResultListApiModel<MediaObjectApiModel>) => {
            if (typeof userApiModel.avatar === 'string') {
              const avatar = mediaObjectResultList.records.find(e => e['@id'] === userApiModel.avatar);
              userVM.avatar = avatar ? new MediaObjectViewModel(avatar) : null;
            }
            if (typeof userApiModel.background === 'string') {
              const background = mediaObjectResultList.records.find(e => e['@id'] === userApiModel.background);
              userVM.background = background ? new MediaObjectViewModel(background) : null;
            }
            return userVM;
          }), catchError(() => of(userVM)));
      })
    );
  }

  public getUserWithMediaBySlug(slug: string, options?: ResourcePageRequestOptions): Observable<UserViewModel> {
    return this.getUserWithMedia(`${this.us.resource}/${slug}`, options);
  }

  public updateUser(userViewModel: UserViewModel): Observable<UserViewModel> {
    return this.us.put(userViewModel["@id"], userViewModel.toApiModel())
      .pipe(map((userApiModel: UserApiModel) => new UserViewModel(userApiModel)));
  }

  public updateUserSocialProfileLinks(userViewModel: UserViewModel): Observable<UserViewModel> {
    return this.us.putSocialLinks(userViewModel["@id"], userViewModel.toApiModel())
      .pipe(map((userApiModel: UserApiModel) => new UserViewModel(userApiModel)));
  }

  public updateSettings(userViewModel: UserViewModel): Observable<UserViewModel> {
    return this.settingsWriter.patch(userViewModel.settings)
      .pipe(map((userApiModel: UserApiModel) => new UserViewModel(userApiModel)));
  }

  public changePassword(userViewModel: UserPasswordViewModel) {
    return this.us.changePassword(userViewModel.toPasswordApiModel())
  }

  public deleteAccount(password: string) {
    return this.us.deleteAccount(password);
  }

  public updateUserAvatar(currentUserViewModel: CurrentUserViewModel, file: File): Observable<CurrentUserViewModel> {
    return this.mors.uploadImage(file)
      .pipe(mergeMap((mediaObjectApiModel: MediaObjectApiModel) => {
        const currentUserApiModel = Object.assign(new UserApiModel(), currentUserViewModel.toApiModel());
        currentUserApiModel.avatar = mediaObjectApiModel['@id'];
        return this.us.patch(currentUserViewModel['@id'], currentUserApiModel)
          .pipe(map((userApiModel: UserApiModel) => {
            currentUserViewModel.apiModel.avatar = userApiModel.avatar;
            currentUserViewModel.avatar = new MediaObjectViewModel(mediaObjectApiModel);
            return currentUserViewModel;
          }))
      }));
  }

  public removeUserAvatar(currentUserViewModel: CurrentUserViewModel): Observable<CurrentUserViewModel> {
    return this.mors.deleteMediaObject(currentUserViewModel.avatar["@id"])
      .pipe(map(() => {
        currentUserViewModel.avatar = null;
        currentUserViewModel.apiModel.avatar = null;
        return currentUserViewModel;
      }));
  }

  public updateUserBackground(currentUserViewModel: CurrentUserViewModel, file: File): Observable<CurrentUserViewModel> {
    return this.mors.uploadImage(file)
      .pipe(mergeMap((mediaObjectApiModel: MediaObjectApiModel) => {
        const currentUserApiModel = Object.assign(new UserApiModel(), currentUserViewModel.toApiModel());
        currentUserApiModel.background = mediaObjectApiModel['@id'];
        return this.us.patch(currentUserViewModel['@id'], currentUserApiModel)
          .pipe(map((userApiModel: UserApiModel) => {
            currentUserViewModel.apiModel.background = userApiModel.background;
            currentUserViewModel.background = new MediaObjectViewModel(mediaObjectApiModel);
            return currentUserViewModel;
          }))
      }));
  }

  public removeUserBackground(currentUserViewModel: CurrentUserViewModel): Observable<CurrentUserViewModel> {
    return this.mors.deleteMediaObject(currentUserViewModel.background["@id"])
      .pipe(map(() => {
        currentUserViewModel.background = null;
        currentUserViewModel.apiModel.background = null;
        return currentUserViewModel;
      }));
  }
}
